import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MeetingSheet from "@/components/Agenda/MeetingSheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProspectingReport } from "@/components/Crm/ProspectingReport";
import { AddLeadsDialog } from "@/components/Crm/AddLeadsDialog";
import TransferDealDialog from "@/components/Crm/TransferDealDialog";
import {
  QUEUE_BUCKETS,
  QUEUE_BUCKET_LABELS,
  renderScript,
  scriptsForAttempt,
  type QueueBucket,
} from "@shared/prospecting";
import {
  AlertTriangle,
  ArrowRightLeft,
  Building2,
  CalendarCheck,
  Check,
  ChevronDown,
  ExternalLink,
  ListFilter,
  MessageCircle,
  MoreHorizontal,
  PhoneOff,
  Plus,
  Search,
  Send,
  Settings,
  ThumbsDown,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

function formatDay(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/**
 * A fila do SDR.
 *
 * O botão "Abordar no WhatsApp" abre uma aba vazia SINCRONAMENTE no clique
 * (senão o bloqueador de pop-up engole), registra a tentativa no servidor e só
 * então aponta a aba para o WhatsApp Web com o roteiro. Se o navegador não deixar
 * abrir a aba, o link fica no toast para clicar à mão.
 */
export default function Prospecting() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:prospecting_view">
        <ProspectingInner />
      </PermissionGuard>
    </DashboardLayout>
  );
}

function ProspectingInner() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const utils = trpc.useUtils();
  const canViewAll = can("crm:deals_view_all");
  const canManage = can("crm:prospecting_manage");
  const canAssign = can("crm:deals_assign");

  const pipelinesQuery = trpc.prospecting.pipelines.list.useQuery();
  const pipelines = pipelinesQuery.data ?? [];
  const [pipelineId, setPipelineId] = useState<number | null>(null);
  useEffect(() => {
    if (pipelineId == null && pipelines.length > 0) setPipelineId(pipelines[0].pipeline.id);
  }, [pipelines, pipelineId]);

  const [owner, setOwner] = useState<"me" | "all" | number>("me");
  const [bucket, setBucket] = useState<QueueBucket>("atrasados");
  // Filtro por lista: "all" = todas; "none" = quem entrou sem lista.
  const [batch, setBatch] = useState<"all" | "none" | number>("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const searching = debouncedSearch.length > 0;
  // Roteiro escolhido por lead (id) — null = sem roteiro. Sem escolha, a
  // primeira opção da tentativa daquele lead.
  const [scriptByDeal, setScriptByDeal] = useState<Record<number, number | null>>({});
  const [tab, setTab] = useState<"fila" | "relatorio">("fila");
  const [showPreview, setShowPreview] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const usersQuery = trpc.prospecting.users.list.useQuery(undefined, { enabled: canViewAll });
  const scriptsQuery = trpc.prospecting.scripts.list.useQuery();
  const scripts = scriptsQuery.data ?? [];

  const queueQuery = trpc.prospecting.queue.list.useQuery(
    {
      pipelineId: pipelineId ?? 0,
      ownerUserId: owner,
      importBatchId: batch === "all" ? undefined : batch,
      search: debouncedSearch || undefined,
    },
    { enabled: pipelineId != null, refetchInterval: 60_000, placeholderData: (prev) => prev },
  );
  const queue = queueQuery.data;

  useContextoDeLista({
    titulo: "Fila de prospecção",
    itens: (queue?.rows ?? []).map((r: any) => ({
      dealId: r.deal?.id,
      titulo: r.deal?.title ?? null,
      contato: r.contact?.name ?? null,
      telefone: r.contact?.phoneNumber ?? null,
      tentativas: r.deal?.attemptCount ?? null,
      proximaTentativa: r.deal?.nextAttemptAt ?? null,
    })),
    extra: { porBucket: queue?.counts ?? null },
  });


  const batchesQuery = trpc.prospecting.queue.batches.useQuery(
    { pipelineId: pipelineId ?? 0, ownerUserId: owner },
    { enabled: pipelineId != null },
  );
  const batches = batchesQuery.data?.batches ?? [];
  const withoutBatch = batchesQuery.data?.withoutBatch ?? 0;
  // Lista escolhida sumiu da fila (acabou ou trocou o dono/funil) → volta para todas.
  useEffect(() => {
    if (typeof batch === "number" && batchesQuery.data && !batches.some((b) => b.id === batch)) setBatch("all");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchesQuery.data]);

  // Primeiro balde com gente vira a aba inicial — abrir em "Atrasados" vazio
  // quando há 30 novos esperando é começar o dia olhando para o nada.
  useEffect(() => {
    if (!queue) return;
    if (queue.counts[bucket] > 0) return;
    const first = QUEUE_BUCKETS.find((b) => queue.counts[b] > 0);
    if (first) setBucket(first);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue?.counts]);

  const invalidate = () => {
    utils.prospecting.queue.list.invalidate();
    utils.prospecting.queue.batches.invalidate();
    utils.prospecting.report.summary.invalidate();
    utils.prospecting.report.byScript.invalidate();
  };

  const recordMut = trpc.prospecting.attempt.record.useMutation({ onSuccess: invalidate });
  const outcomeMut = trpc.prospecting.outcome.set.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const bulkNoReplyMut = trpc.prospecting.outcome.bulkNoReply.useMutation({
    onSuccess: (r) => {
      toast.success(`${r.done} lead${r.done === 1 ? "" : "s"} marcado${r.done === 1 ? "" : "s"} como sem resposta`);
      invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const [transferFor, setTransferFor] = useState<number | null>(null);
  const [meetingFor, setMeetingFor] = useState<{ dealId: number; contactId: number; contactName: string | null; ownerUserId: number | null } | null>(null);
  const [meetingSheet, setMeetingSheet] = useState<{ contactId: number; contactName: string | null; dealId: number; attendeeUserIds: number[] } | null>(null);

  const meuNome = ((user as any)?.name as string | undefined) ?? "";

  async function abordar(row: NonNullable<typeof queue>["rows"][number]) {
    try {
      const chosen = scriptByDeal[row.deal.id];
      // Lista de roteiros ainda carregando → undefined (servidor pega a
      // primeira opção da tentativa), nunca null ("sem roteiro").
      const scriptId = chosen !== undefined
        ? chosen
        : scriptsQuery.isLoading
          ? undefined
          : (scriptsForAttempt(scripts, row.deal.attemptCount + 1)[0]?.id ?? null);
      const res = await recordMut.mutateAsync({ dealId: row.deal.id, scriptId });
      // A escolha valia para ESTA tentativa; a próxima tem outras opções.
      setScriptByDeal((m) => { const { [row.deal.id]: _, ...rest } = m; return rest; });
      // whatsapp:// abre o app sem sair da página — não precisa de aba nova.
      // O botão do aviso cobre o navegador que segurar a abertura depois do await.
      window.location.href = res.waUrl;
      toast.info("Abrindo a conversa no app do WhatsApp", {
        duration: 15000,
        action: { label: "Abrir de novo", onClick: () => { window.location.href = res.waUrl; } },
      });
      toast.success(
        res.nextAttemptAt
          ? `Tentativa ${res.attemptNo} registrada. Próxima: ${formatDay(res.nextAttemptAt)}`
          : `Tentativa ${res.attemptNo} registrada — era a última da cadência`,
      );
    } catch (e) {
      toast.error(toUserFriendlyError(e as any));
    }
  }

  // Buscando, a lista ignora as abas: o servidor já devolveu ranqueado por
  // proximidade do nome, e o balde vira etiqueta em cada linha.
  const rows = useMemo(
    () => (searching ? queue?.rows ?? [] : (queue?.rows ?? []).filter((r) => r.bucket === bucket)),
    [queue, bucket, searching],
  );
  const readyIds = queue?.readyForNoReply ?? [];

  if (pipelinesQuery.isLoading) {
    return <div className="p-6 space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (pipelines.length === 0) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-center max-w-md mx-auto">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Send className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-semibold">Fila de prospecção</h2>
          <p className="text-sm text-muted-foreground">
            Importe os leads, aborde cada um pelo WhatsApp com um clique e o CRM registra quem foi
            tentado, quem respondeu e quem agendou reunião. Nenhum lead se perde mais.
          </p>
          {canManage ? (
            <Button asChild>
              <Link href="/settings?tab=crm">
                <Settings className="h-4 w-4 mr-2" /> Configurar a fila
              </Link>
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Peça ao administrador para configurar a fila em Configurações → CRM.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Send className="h-5 w-5 text-primary" /> Prospecção
        </h1>
        {/* Abas no cabeçalho: uma linha a menos entre o título e a fila. */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList className="h-9">
            <TabsTrigger value="fila">Fila</TabsTrigger>
            <TabsTrigger value="relatorio">Relatório</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {pipelines.length > 1 && (
            <Select value={String(pipelineId ?? "")} onValueChange={(v) => setPipelineId(Number(v))}>
              <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder="Funil" /></SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.pipeline.id} value={String(p.pipeline.id)}>{p.pipeline.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canViewAll && (
            <Select
              value={typeof owner === "number" ? String(owner) : owner}
              onValueChange={(v) => setOwner(v === "me" || v === "all" ? v : Number(v))}
            >
              <SelectTrigger className="h-9 w-[200px]">
                <Users className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Meus leads</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
                {(usersQuery.data ?? []).map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>{u.name ?? u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" className="h-9" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Adicionar leads
          </Button>
        </div>
      </div>

      {pipelineId != null && (
        <AddLeadsDialog
          open={showAdd}
          onClose={() => setShowAdd(false)}
          pipelineId={pipelineId}
          onDone={({ ownerIds }) => {
            // Lista subiu para um comercial específico: mostra a fila dele,
            // senão o gestor fecha o diálogo e vê a própria fila vazia.
            if (canViewAll) {
              if (ownerIds.length === 1 && ownerIds[0] !== (user as any)?.id) setOwner(ownerIds[0]);
              else if (ownerIds.length > 1) setOwner("all");
              else setOwner("me"); // nenhum escolhido = entraram na MINHA fila
            }
            setBatch("all");
            setSearch("");
            setBucket("novos");
          }}
        />
      )}

      <Tabs value={tab}>
        <TabsContent value="fila" className="space-y-2 mt-0">
          {readyIds.length > 0 && (
            <Card className="border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20">
              <CardContent className="px-3 py-2 flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-sm">
                  <strong>{readyIds.length}</strong> lead{readyIds.length === 1 ? "" : "s"} esgotaram as tentativas sem responder.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto h-8"
                  disabled={bulkNoReplyMut.isPending}
                  onClick={() => bulkNoReplyMut.mutate({ dealIds: readyIds })}
                >
                  <PhoneOff className="h-3.5 w-3.5 mr-1.5" />
                  Marcar todos como Sem resposta
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {QUEUE_BUCKETS.map((b) => (
                <Button
                  key={b}
                  size="sm"
                  variant={!searching && bucket === b ? "default" : "outline"}
                  className="h-8"
                  disabled={searching}
                  title={searching ? "Limpe a busca para navegar pelas abas" : undefined}
                  onClick={() => setBucket(b)}
                >
                  {QUEUE_BUCKET_LABELS[b]}
                  <Badge variant={!searching && bucket === b ? "secondary" : "outline"} className="ml-1.5 h-5 px-1.5 text-[11px]">
                    {queue?.counts[b] ?? 0}
                  </Badge>
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <div className="relative w-[240px]">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome, empresa ou cidade"
                  maxLength={120}
                  className="h-8 pl-8 pr-8 text-sm"
                />
                {search && (
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearch("")}
                    aria-label="Limpar busca"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {(batches.length > 0 || batch !== "all") && (
                <Select
                  value={typeof batch === "number" ? String(batch) : batch}
                  onValueChange={(v) => setBatch(v === "all" || v === "none" ? v : Number(v))}
                >
                  <SelectTrigger className="h-8 w-[220px] text-sm">
                    <ListFilter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" />
                    <SelectValue placeholder="Lista" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as listas</SelectItem>
                    {batches.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        <span className="flex items-center gap-2">
                          <span className="truncate max-w-[180px]">{b.name}</span>
                          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {formatDay(b.createdAt)} · {b.count}{b.ownerName && owner === "all" ? ` · ${b.ownerName}` : ""}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                    {withoutBatch > 0 && (
                      <>
                        <SelectSeparator />
                        <SelectItem value="none">
                          <span className="flex items-center gap-2">
                            Sem lista <span className="text-[11px] text-muted-foreground">{withoutBatch}</span>
                          </span>
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              )}
              {scripts.length > 0 && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowPreview((v) => !v)}>
                  {showPreview ? "Ocultar prévia" : "Prévia do roteiro"}
                </Button>
              )}
            </div>
          </div>

          {queueQuery.isLoading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-14 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
              {searching
                ? `Ninguém parecido com "${debouncedSearch}" na fila.`
                : batch !== "all"
                  ? `Nada em "${QUEUE_BUCKET_LABELS[bucket]}" nesta lista.`
                  : bucket === "novos"
                    ? "Nenhum lead novo. Clique em \"Adicionar leads\" para importar uma planilha ou escolher contatos."
                    : `Nada em "${QUEUE_BUCKET_LABELS[bucket]}".`}
            </div>
          ) : (
            <div className="rounded-lg border divide-y bg-card">
              {rows.map((r) => {
                const nome = [r.contact?.name, r.contact?.lastName].filter(Boolean).join(" ") || r.deal.title;
                const options = scriptsForAttempt(scripts, r.deal.attemptCount + 1);
                const chosen = scriptByDeal[r.deal.id];
                const scriptForCard = chosen === undefined ? options[0] ?? null : chosen === null ? null : scripts.find((s) => s.id === chosen) ?? null;
                const preview = scriptForCard
                  ? renderScript(scriptForCard.body, {
                      nome: r.contact?.name,
                      nome_completo: nome,
                      empresa: r.contact?.companyName,
                      vendedor: meuNome,
                    })
                  : null;
                const busy = recordMut.isPending || outcomeMut.isPending;
                return (
                  <div key={r.deal.id} className="px-3 py-2 flex flex-col md:flex-row md:items-center gap-2 hover:bg-muted/30">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <Link href={`/crm/deals/${r.deal.id}`} className="font-medium text-sm hover:underline truncate">{nome}</Link>
                          {r.contact?.companyName && (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate">
                              <Building2 className="h-3 w-3" /> {r.contact.companyName}
                            </span>
                          )}
                          {r.contact?.phoneNumber && (
                            <span className="text-xs text-muted-foreground font-mono">{r.contact.phoneNumber}</span>
                          )}
                          <span className="text-[11px] text-muted-foreground">
                            · tentativa <strong>{r.deal.attemptCount}</strong>/{queue?.config.maxAttempts}
                          </span>
                          {r.deal.lastAttemptAt && <span className="text-[11px] text-muted-foreground">· última {formatDay(r.deal.lastAttemptAt)}</span>}
                          {r.deal.nextAttemptAt && r.bucket !== "esgotados" && (
                            <span className={`text-[11px] ${r.bucket === "atrasados" ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                              · próxima {formatDay(r.deal.nextAttemptAt)}
                            </span>
                          )}
                          {owner !== "me" && r.owner?.name && <span className="text-[11px] text-muted-foreground">· {r.owner.name}</span>}
                          {searching && (
                            <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
                              {QUEUE_BUCKET_LABELS[r.bucket]}
                            </Badge>
                          )}
                        </div>
                        {preview && showPreview && (
                          <p className="mt-0.5 text-xs text-muted-foreground/90 line-clamp-2 italic">"{preview}"</p>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5 md:justify-end shrink-0">
                        {scripts.length > 0 && (
                          <Select
                            value={scriptForCard ? String(scriptForCard.id) : "none"}
                            onValueChange={(v) => setScriptByDeal((m) => ({ ...m, [r.deal.id]: v === "none" ? null : Number(v) }))}
                          >
                            <SelectTrigger className="h-7 w-[170px] text-xs" title={`Roteiros da ${r.deal.attemptCount + 1}ª tentativa`}>
                              <SelectValue placeholder="Roteiro" />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((s) => (
                                <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                              ))}
                              {options.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">Nenhum roteiro para a {r.deal.attemptCount + 1}ª tentativa</div>
                              )}
                              <SelectSeparator />
                              <SelectItem value="none">Sem roteiro</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          size="sm"
                          className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={busy || !r.contact?.phoneNumber}
                          onClick={() => abordar(r)}
                          title={r.deal.attemptCount >= (queue?.config.maxAttempts ?? 0) ? "Cadência esgotada — registra uma tentativa extra" : undefined}
                        >
                          <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                          {r.deal.attemptCount === 0 ? "Abordar no WhatsApp" : "Abordar de novo"}
                          <ExternalLink className="h-3 w-3 ml-1.5 opacity-70" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7" disabled={busy}
                          onClick={() => outcomeMut.mutate({ dealId: r.deal.id, outcome: "replied" }, { onSuccess: () => toast.success(`${nome}: respondeu`) })}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Respondeu
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" disabled={busy}>
                              Resultado <ChevronDown className="h-3.5 w-3.5 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel className="text-xs">{nome}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setMeetingFor({ dealId: r.deal.id, contactId: r.deal.contactId, contactName: nome, ownerUserId: r.deal.ownerUserId })}>
                              <CalendarCheck className="h-3.5 w-3.5 mr-2" /> Reunião agendada
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => outcomeMut.mutate({ dealId: r.deal.id, outcome: "not_interested" }, { onSuccess: () => toast.success(`${nome}: sem interesse`) })}>
                              <ThumbsDown className="h-3.5 w-3.5 mr-2" /> Sem interesse
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => outcomeMut.mutate({ dealId: r.deal.id, outcome: "invalid_number" }, { onSuccess: () => toast.success(`${nome}: número inválido`) })}>
                              <PhoneOff className="h-3.5 w-3.5 mr-2" /> Número inválido
                            </DropdownMenuItem>
                            {r.bucket === "esgotados" ? (
                              <DropdownMenuItem onClick={() => outcomeMut.mutate({ dealId: r.deal.id, outcome: "no_reply" }, { onSuccess: () => toast.success(`${nome}: sem resposta`) })}>
                                <PhoneOff className="h-3.5 w-3.5 mr-2 opacity-0" /> Sem resposta
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem disabled>
                                <span className="text-xs">Sem resposta: só após {queue?.config.maxAttempts} tentativas ({r.deal.attemptCount} feitas)</span>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" disabled={busy} aria-label="Mais ações">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {canAssign && (
                              <DropdownMenuItem onClick={() => setTransferFor(r.deal.id)}>
                                <ArrowRightLeft className="h-3.5 w-3.5 mr-2" /> Transferir para outro comercial
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem asChild>
                              <Link href={`/crm/deals/${r.deal.id}`}>
                                <ExternalLink className="h-3.5 w-3.5 mr-2" /> Abrir negociação
                              </Link>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="relatorio" className="mt-0">
          {pipelineId != null && (
            <ProspectingReport
              pipelineId={pipelineId}
              canViewAll={canViewAll}
              users={usersQuery.data ?? []}
            />
          )}
        </TabsContent>
      </Tabs>

      <MeetingOutcomeDialog
        target={meetingFor}
        users={usersQuery.data ?? []}
        canViewAll={canViewAll}
        pending={outcomeMut.isPending}
        onClose={() => setMeetingFor(null)}
        onConfirm={(salesOwnerUserId) => {
          if (!meetingFor) return;
          outcomeMut.mutate(
            { dealId: meetingFor.dealId, outcome: "meeting", salesOwnerUserId },
            {
              onSuccess: (res) => {
                toast.success(res.salesDealId ? "Reunião agendada — oportunidade criada no funil de vendas" : "Reunião agendada");
                setMeetingFor(null);
                setMeetingSheet({
                  contactId: res.contactId,
                  contactName: res.contactName,
                  dealId: res.salesDealId ?? res.dealId,
                  attendeeUserIds: salesOwnerUserId ? [salesOwnerUserId] : [],
                });
              },
            },
          );
        }}
      />

      {/* Mesmo diálogo do CRM: quem entrega fica no time, o contato acompanha o bastão. */}
      {transferFor != null && (
        <TransferDealDialog
          dealId={transferFor}
          open
          onClose={() => setTransferFor(null)}
          onDone={invalidate}
        />
      )}

      <MeetingSheet
        open={meetingSheet != null}
        onClose={() => setMeetingSheet(null)}
        defaults={meetingSheet ?? undefined}
      />
    </div>
  );
}

/** "Reunião agendada": quem vai ser o dono da oportunidade de venda? */
function MeetingOutcomeDialog({
  target,
  users,
  canViewAll,
  pending,
  onClose,
  onConfirm,
}: {
  target: { dealId: number; contactName: string | null; ownerUserId: number | null } | null;
  users: Array<{ id: number; name: string | null; email: string | null }>;
  canViewAll: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: (salesOwnerUserId: number | null) => void;
}) {
  const { user } = useAuth();
  const meuId = (user as any)?.id as number | undefined;
  const [ownerId, setOwnerId] = useState<string>("");
  useEffect(() => {
    if (target) setOwnerId(String(target.ownerUserId ?? meuId ?? ""));
  }, [target, meuId]);

  return (
    <Dialog open={target != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reunião agendada</DialogTitle>
          <DialogDescription>
            {target?.contactName ?? "O lead"} vai para "Reunião agendada" e nasce uma oportunidade no
            funil de vendas. Em seguida você marca a reunião na agenda.
          </DialogDescription>
        </DialogHeader>
        {canViewAll && users.length > 0 ? (
          <div className="space-y-1.5">
            <Label className="text-xs">Vendedor responsável pela oportunidade</Label>
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Escolha" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>{u.name ?? u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">A oportunidade fica com você. O gestor pode repassá-la para a closer.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>Cancelar</Button>
          <Button onClick={() => onConfirm(ownerId ? Number(ownerId) : null)} disabled={pending}>
            <CalendarCheck className="h-4 w-4 mr-1.5" /> Confirmar e marcar reunião
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
